import { useState } from 'react';
import type { ApiEndpoints } from '@avisoma/shared';
import { useQuery } from '@tanstack/react-query';
import backend from '../../services/backend';
import { Card } from '../../components/Card';
import { TableSkeleton } from '../../components/Skeleton';
import { IconCross } from '../../icons/IconCross';
import IconSearch from '../../icons/IconSearch';
import { DocumentTypeCard } from './DocumentTypeCard';
import { DocumentTypeDocumentsDrawer } from './DocumentTypeDocumentsDrawer';

interface DocumentTypeCardsProps {
  projectId: string;
}

type ProjectDocumentType =
  ApiEndpoints<string>['/api/projects/:id/document-types']['GET']['response']['types'][number];

// Only surface the freeform filter once the chip list is long enough to be worth narrowing.
const FILTER_THRESHOLD = 8;

export function DocumentTypeCards({ projectId }: DocumentTypeCardsProps) {
  const [selectedType, setSelectedType] = useState<ProjectDocumentType | null>(null);
  const [search, setSearch] = useState('');

  // This component instance is reused across /projects/:id, so reset per-project
  // view state on navigation — otherwise a filter (or open drawer) leaks into the
  // next project and can strand it in a "no matches" state with no visible input.
  const [prevProjectId, setPrevProjectId] = useState(projectId);
  if (prevProjectId !== projectId) {
    setPrevProjectId(projectId);
    setSearch('');
    setSelectedType(null);
  }

  const typesQuery = useQuery({
    queryKey: ['getProjectDocumentTypes', projectId],
    queryFn: () => backend.getProjectDocumentTypes(projectId).then((res) => res.data),
    enabled: !!projectId,
  });

  const sortedTypes = [...(typesQuery.data?.types ?? [])].sort((a, b) =>
    a.classification.localeCompare(b.classification),
  );
  // Categories prefixed with "-" (e.g. "-Document Section") are structural placeholders,
  // not real document types — push them to the bottom instead of letting ASCII sort them first.
  const documentTypes = sortedTypes.filter((type) => !type.classification.startsWith('-'));
  const structuralTypes = sortedTypes.filter((type) => type.classification.startsWith('-'));

  const query = search.trim().toLowerCase();
  const matchesQuery = (type: ProjectDocumentType) => type.classification.toLowerCase().includes(query);
  const filteredDocumentTypes = query ? documentTypes.filter(matchesQuery) : documentTypes;
  const filteredStructuralTypes = query ? structuralTypes.filter(matchesQuery) : structuralTypes;
  const hasMatches = filteredDocumentTypes.length > 0 || filteredStructuralTypes.length > 0;

  const showFilter = sortedTypes.length > FILTER_THRESHOLD;

  return (
    <Card
      header={
        <div className="flex items-center justify-between gap-4">
          <h2 className="text-lg font-medium text-gray-900 dark:text-zinc-100">Reconstructed Documents</h2>
          {showFilter && (
            <div className="relative w-56 max-w-[50%]">
              <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-2.5">
                <IconSearch className="h-4 w-4 text-gray-400" />
              </div>
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Filter document types…"
                aria-label="Filter document types"
                className="block w-full rounded-md border border-gray-300 py-1.5 pl-8 pr-8 text-sm focus:border-blue-500 focus:outline-none focus:ring-blue-500 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-100 dark:focus:border-blue-400 dark:focus:ring-blue-400"
              />
              {search && (
                <button
                  type="button"
                  onClick={() => setSearch('')}
                  aria-label="Clear filter"
                  className="absolute inset-y-0 right-0 flex items-center pr-2.5"
                >
                  <IconCross className="h-4 w-4 text-gray-400 hover:text-gray-600 dark:hover:text-zinc-300" />
                </button>
              )}
            </div>
          )}
        </div>
      }
    >
      {typesQuery.isLoading ? (
        <TableSkeleton />
      ) : typesQuery.error ? (
        <p className="text-sm text-red-600 dark:text-red-400">{(typesQuery.error as Error).message}</p>
      ) : sortedTypes.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-zinc-400">No documents reconstructed yet</p>
      ) : !hasMatches ? (
        <p className="text-sm text-gray-500 dark:text-zinc-400">
          No document types match “{search.trim()}”.
        </p>
      ) : (
        <div className="flex max-h-64 flex-wrap gap-2 overflow-y-auto">
          {filteredDocumentTypes.map((type) => (
            <DocumentTypeCard
              key={type.classification}
              classification={type.classification}
              documentCount={type.documentCount}
              onClick={() => setSelectedType(type)}
            />
          ))}
          {filteredDocumentTypes.length > 0 && filteredStructuralTypes.length > 0 && (
            <div className="basis-full h-0" aria-hidden />
          )}
          {filteredStructuralTypes.map((type) => (
            <DocumentTypeCard
              key={type.classification}
              classification={type.classification}
              documentCount={type.documentCount}
              onClick={() => setSelectedType(type)}
            />
          ))}
        </div>
      )}

      <DocumentTypeDocumentsDrawer
        open={selectedType != null}
        onClose={() => setSelectedType(null)}
        projectId={projectId}
        type={selectedType}
      />
    </Card>
  );
}
